/**
 * useReception Hook
 * State management for reception list
 *
 * @version 2025.1.0
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useSnackbar } from 'notistack';
import {
  ReceptionAppointment,
  ReceptionFilters,
  ReceptionSummary,
  AttendanceType,
} from '../types/reception';
import { TriageData } from '../types/triage';
import receptionService from '../services/receptionService';
import { getSocket } from '../services/socket';
import { prerequisiteFromError } from '../utils/prerequisiteFromError';
import type { PrerequisiteBlockProps } from '../components/common';

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface UseReceptionOptions {
  initialFilters?: ReceptionFilters;
  autoLoad?: boolean;
  enableRealtime?: boolean; // Socket.IO real-time (default: true)
}

interface UseReceptionReturn {
  // State
  appointments: ReceptionAppointment[];
  summary: ReceptionSummary | null;
  loading: boolean;
  summaryLoading: boolean;
  filters: ReceptionFilters;
  pagination: Pagination;
  socketConnected: boolean;

  // Filter actions
  setFilters: (filters: ReceptionFilters) => void;
  updateFilter: <K extends keyof ReceptionFilters>(key: K, value: ReceptionFilters[K]) => void;
  clearFilters: () => void;

  // Pagination actions
  setPage: (page: number) => void;
  setLimit: (limit: number) => void;

  // Data actions
  loadData: () => Promise<void>;
  loadSummary: () => Promise<void>;
  refresh: () => Promise<void>;

  // Attendance actions
  checkIn: (appointmentId: number) => Promise<boolean>;
  saveTriageOnly: (appointmentId: number, triageData: TriageData) => Promise<boolean>;
  startAttendance: (appointmentId: number) => Promise<boolean>;
  // `onPrerequisiteBlock` é opcional: quem chama sem ele mantém o toast genérico de hoje.
  // A visualização de calendário passa o callback para render um PrerequisiteBlock no lugar.
  completeAttendance: (
    appointmentId: number,
    attendanceType?: AttendanceType,
    onPrerequisiteBlock?: (block: PrerequisiteBlockProps) => void
  ) => Promise<boolean>;
  markNoShow: (appointmentId: number) => Promise<boolean>;

  // Queue management actions
  updateQueueNumber: (appointmentId: number, newQueueNumber: number) => Promise<boolean>;
  resetQueue: (appointmentId: number) => Promise<boolean>;
}

const defaultFilters: ReceptionFilters = {
  date: new Date().toISOString().split('T')[0], // Today
  period: 'all',
};

const defaultPagination: Pagination = {
  page: 1,
  limit: 50,
  total: 0,
  totalPages: 0,
};

export function useReception(options: UseReceptionOptions = {}): UseReceptionReturn {
  const { initialFilters = defaultFilters, autoLoad = true, enableRealtime = true } = options;
  const { enqueueSnackbar } = useSnackbar();

  // State
  const [appointments, setAppointments] = useState<ReceptionAppointment[]>([]);
  const [summary, setSummary] = useState<ReceptionSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [filters, setFiltersState] = useState<ReceptionFilters>(initialFilters);
  const [pagination, setPagination] = useState<Pagination>(defaultPagination);
  const [socketConnected, setSocketConnected] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load appointments list
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await receptionService.getReceptionList({
        ...filters,
        page: pagination.page,
        limit: pagination.limit,
      });

      setAppointments(result.appointments);
      setPagination(prev => ({
        ...prev,
        total: result.total,
        totalPages: result.totalPages,
      }));
    } catch (error) {
      console.error('Error loading reception list:', error);
      enqueueSnackbar('Erro ao carregar lista de recepção', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.limit, enqueueSnackbar]);

  // Calculate summary from appointments when clinic is not selected
  const calculateLocalSummary = useCallback((appointmentsList: ReceptionAppointment[]): ReceptionSummary => {
    const byStatus: Record<string, number> = {
      NOT_ATTENDED: 0,
      CHECKED_IN: 0,
      TRIAGED: 0,
      IN_PROGRESS: 0,
      COMPLETED: 0,
      NO_SHOW: 0,
    };

    let morningCount = 0;
    let afternoonCount = 0;

    for (const appt of appointmentsList) {
      // Count by attendance status
      const status = appt.attendanceStatus ?? 'NOT_ATTENDED';
      byStatus[status] = (byStatus[status] || 0) + 1;

      // Count by period — Ordem de Chegada uses period field, Agenda dos Profissionais uses hour
      if (appt.period === 'morning') {
        morningCount++;
      } else if (appt.period === 'afternoon') {
        afternoonCount++;
      } else {
        const hour = parseInt(appt.hour ?? '0', 10);
        if (hour < 12) {
          morningCount++;
        } else {
          afternoonCount++;
        }
      }
    }

    return {
      total: appointmentsList.length,
      morning: morningCount,
      afternoon: afternoonCount,
      byStatus,
    };
  }, []);

  // Load summary
  const loadSummary = useCallback(async () => {
    // If no clinic is selected, calculate summary from loaded appointments
    if (!filters.clinicId) {
      if (appointments.length > 0) {
        setSummary(calculateLocalSummary(appointments));
      }
      return;
    }

    if (!filters.date) return;

    setSummaryLoading(true);
    try {
      const result = await receptionService.getReceptionSummary(filters.clinicId, filters.date);
      setSummary(result);
    } catch (error) {
      console.error('Error loading reception summary:', error);
      // Don't show error for summary, it's not critical
    } finally {
      setSummaryLoading(false);
    }
  }, [filters.clinicId, filters.date, appointments, calculateLocalSummary]);

  // Refresh all data
  const refresh = useCallback(async () => {
    await Promise.all([loadData(), loadSummary()]);
  }, [loadData, loadSummary]);

  // Filter actions
  const setFilters = useCallback((newFilters: ReceptionFilters) => {
    setFiltersState(newFilters);
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to page 1 on filter change
  }, []);

  const updateFilter = useCallback(<K extends keyof ReceptionFilters>(key: K, value: ReceptionFilters[K]) => {
    setFiltersState(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to page 1 on filter change
  }, []);

  const clearFilters = useCallback(() => {
    setFiltersState(defaultFilters);
    setPagination(prev => ({ ...prev, page: 1 }));
  }, []);

  // Pagination actions
  const setPage = useCallback((page: number) => {
    setPagination(prev => ({ ...prev, page }));
  }, []);

  const setLimit = useCallback((limit: number) => {
    setPagination(prev => ({ ...prev, limit, page: 1 }));
  }, []);

  // Helper to update appointment in list after action
  const updateAppointmentInList = useCallback((updatedAppointment: ReceptionAppointment) => {
    setAppointments(prev =>
      prev.map(apt => (apt.id === updatedAppointment.id ? updatedAppointment : apt))
    );
  }, []);

  // Attendance actions
  const checkIn = useCallback(async (appointmentId: number): Promise<boolean> => {
    try {
      const updated = await receptionService.checkIn(appointmentId);
      updateAppointmentInList(updated);
      enqueueSnackbar('Check-in realizado com sucesso', { variant: 'success' });
      loadSummary(); // Refresh summary
      return true;
    } catch (error) {
      console.error('Error during check-in:', error);
      enqueueSnackbar('Erro ao realizar check-in', { variant: 'error' });
      return false;
    }
  }, [updateAppointmentInList, loadSummary, enqueueSnackbar]);

  const saveTriageOnly = useCallback(async (appointmentId: number, triageData: TriageData): Promise<boolean> => {
    try {
      const updated = await receptionService.saveTriageOnly(appointmentId, {
        bloodPressureSystolic: triageData.bloodPressureSystolic,
        bloodPressureDiastolic: triageData.bloodPressureDiastolic,
        heartRate: triageData.heartRate,
        temperature: triageData.temperature,
        weight: triageData.weight,
        height: triageData.height,
        oxygenSaturation: triageData.oxygenSaturation,
        observations: triageData.observations ?? null,
      });
      updateAppointmentInList(updated);
      enqueueSnackbar('Triagem registrada com sucesso', { variant: 'success' });
      loadSummary(); // Refresh summary
      return true;
    } catch (error) {
      console.error('Error saving triage:', error);
      enqueueSnackbar('Erro ao registrar triagem', { variant: 'error' });
      return false;
    }
  }, [updateAppointmentInList, loadSummary, enqueueSnackbar]);

  const startAttendance = useCallback(async (appointmentId: number): Promise<boolean> => {
    try {
      const updated = await receptionService.startAttendance(appointmentId);
      updateAppointmentInList(updated);
      enqueueSnackbar('Atendimento iniciado', { variant: 'success' });
      loadSummary(); // Refresh summary
      return true;
    } catch (error) {
      console.error('Error starting attendance:', error);
      enqueueSnackbar('Erro ao iniciar atendimento', { variant: 'error' });
      return false;
    }
  }, [updateAppointmentInList, loadSummary, enqueueSnackbar]);

  const completeAttendance = useCallback(async (
    appointmentId: number,
    attendanceType?: AttendanceType,
    onPrerequisiteBlock?: (block: PrerequisiteBlockProps) => void
  ): Promise<boolean> => {
    try {
      const updated = await receptionService.completeAttendance(appointmentId, attendanceType);
      updateAppointmentInList(updated);
      const message = attendanceType === AttendanceType.QUICK
        ? 'Atendimento rapido concluido'
        : 'Atendimento concluido';
      enqueueSnackbar(message, { variant: 'success' });
      loadSummary(); // Refresh summary
      return true;
    } catch (error) {
      console.error('Error completing attendance:', error);
      const prerequisiteBlock = prerequisiteFromError(error);
      if (prerequisiteBlock && onPrerequisiteBlock) {
        onPrerequisiteBlock(prerequisiteBlock);
        return false;
      }
      enqueueSnackbar('Erro ao concluir atendimento', { variant: 'error' });
      return false;
    }
  }, [updateAppointmentInList, loadSummary, enqueueSnackbar]);

  const markNoShow = useCallback(async (appointmentId: number): Promise<boolean> => {
    try {
      const updated = await receptionService.markNoShow(appointmentId);
      updateAppointmentInList(updated);
      enqueueSnackbar('Marcado como falta', { variant: 'info' });
      loadSummary(); // Refresh summary
      return true;
    } catch (error) {
      console.error('Error marking no-show:', error);
      enqueueSnackbar('Erro ao marcar como falta', { variant: 'error' });
      return false;
    }
  }, [updateAppointmentInList, loadSummary, enqueueSnackbar]);

  // Queue management actions
  const updateQueueNumber = useCallback(async (appointmentId: number, newQueueNumber: number): Promise<boolean> => {
    try {
      const updated = await receptionService.updateQueueNumber(appointmentId, newQueueNumber);
      updateAppointmentInList(updated);
      enqueueSnackbar('Senha atualizada com sucesso', { variant: 'success' });
      loadSummary(); // Refresh summary
      return true;
    } catch (error) {
      console.error('Error updating queue number:', error);
      enqueueSnackbar('Erro ao atualizar senha', { variant: 'error' });
      return false;
    }
  }, [updateAppointmentInList, loadSummary, enqueueSnackbar]);

  const resetQueue = useCallback(async (appointmentId: number): Promise<boolean> => {
    try {
      const updated = await receptionService.resetQueue(appointmentId);
      updateAppointmentInList(updated);
      enqueueSnackbar('Senha removida', { variant: 'info' });
      loadSummary(); // Refresh summary
      return true;
    } catch (error) {
      console.error('Error resetting queue:', error);
      enqueueSnackbar('Erro ao resetar senha', { variant: 'error' });
      return false;
    }
  }, [updateAppointmentInList, loadSummary, enqueueSnackbar]);

  // Auto-load on mount and filter/pagination change
  useEffect(() => {
    if (autoLoad) {
      loadData();
    }
  }, [loadData, autoLoad]);

  // Load/calculate summary when clinic, date, or appointments change
  useEffect(() => {
    if (autoLoad && filters.date) {
      // If clinic is selected, load from API; otherwise calculate from appointments
      if (filters.clinicId) {
        loadSummary();
      } else if (appointments.length > 0) {
        // Calculate local summary when no clinic is selected
        setSummary(calculateLocalSummary(appointments));
      }
    }
  }, [loadSummary, autoLoad, filters.clinicId, filters.date, appointments, calculateLocalSummary]);

  // Socket.IO real-time updates
  useEffect(() => {
    if (!enableRealtime) return;

    const socket = getSocket();

    const onConnect = () => {
      socket.emit('join:reception');
      setSocketConnected(true);
    };

    const onDisconnect = () => {
      setSocketConnected(false);
    };

    const onReceptionUpdated = (payload: { clinicId: number; date?: string }) => {
      const clinicMatches = !filters.clinicId || payload.clinicId === filters.clinicId;
      const dateMatches = !filters.date || !payload.date || payload.date === filters.date;

      if (clinicMatches && dateMatches) {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
          loadData();
        }, 300);
      }
    };

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('reception:updated', onReceptionUpdated);

    if (socket.connected) {
      socket.emit('join:reception');
      setSocketConnected(true);
    }

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('reception:updated', onReceptionUpdated);
      socket.emit('leave:reception');
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [enableRealtime, filters.clinicId, filters.date, loadData]);

  // Adaptive polling fallback: 60s when connected (safety net), 10s when disconnected
  useEffect(() => {
    if (!enableRealtime) return;

    const interval = socketConnected ? 60000 : 10000;
    const timer = setInterval(() => {
      loadData();
    }, interval);

    return () => clearInterval(timer);
  }, [enableRealtime, socketConnected, loadData]);

  return {
    // State
    appointments,
    summary,
    loading,
    summaryLoading,
    filters,
    pagination,
    socketConnected,

    // Filter actions
    setFilters,
    updateFilter,
    clearFilters,

    // Pagination actions
    setPage,
    setLimit,

    // Data actions
    loadData,
    loadSummary,
    refresh,

    // Attendance actions
    checkIn,
    saveTriageOnly,
    startAttendance,
    completeAttendance,
    markNoShow,

    // Queue management actions
    updateQueueNumber,
    resetQueue,
  };
}

export default useReception;
